import { forceUntranslate } from '../../services/googleTranslateForceUntranslate';

// Google Translate Manager - Handles all Google Translate operations outside of React
class GoogleTranslateManager {
    private static instance: GoogleTranslateManager;
    private isInitialized: boolean = false;
    private isInitializing: boolean = false;
    private googleTranslateElementId = 'google_translate_element';
    private googleTranslateScriptId = 'google-translate-script';
    private mutationObserver: MutationObserver | null = null;

    private constructor() {}

    static getInstance(): GoogleTranslateManager {
        if (!GoogleTranslateManager.instance) {
            GoogleTranslateManager.instance = new GoogleTranslateManager();
        }
        return GoogleTranslateManager.instance;
    }

    // Initialize Google Translate
    async initialize(languageCode?: string): Promise<boolean> {
        if (this.isInitializing) {
            return false;
        }

        if (this.isInitialized) {
            this.applyLanguage(languageCode);
            return true;
        }

        this.isInitializing = true;

        try {
            // Ensure container exists
            this.ensureContainerExists();

            // Load script if needed
            await this.loadGoogleTranslateScript();

            // Wait for initialization
            const success = await this.waitForGoogleTranslate();
            
            if (success) {
                if (languageCode) {
                    this.applyLanguage(languageCode);
                }
            }

            this.isInitialized = success;
            return success;
        } catch (error) {
            return false;
        } finally {
            this.isInitializing = false;
        }
    }

    // Ensure the container element exists
    private ensureContainerExists(): void {
        let container = document.getElementById(this.googleTranslateElementId);
        if (!container) {
            // Check if portal already created it
            container = document.querySelector('[data-react-portal="true"]#' + this.googleTranslateElementId) as HTMLElement;
        }
        
        if (!container) {
            container = document.createElement('div');
            container.id = this.googleTranslateElementId;
            container.style.display = 'none';
            container.style.position = 'fixed';
            container.style.top = '-9999px';
            container.style.left = '-9999px';
            container.style.zIndex = '-1';
            // Add data attribute to mark as non-React element
            container.setAttribute('data-google-translate', 'true');
            document.body.appendChild(container);
        }
        
        // Start observing for unauthorized removals
        this.protectContainer();
    }

    // Load Google Translate script
    private loadGoogleTranslateScript(): Promise<void> {
        return new Promise((resolve) => {
            // Check if widget already exists first
            const existingWidget = document.querySelector('.goog-te-combo');
            if (existingWidget) {
                this.isInitialized = true;
                resolve();
                return;
            }
            
            // Always set up the init callback first
            if (!(window as any).googleTranslateElementInit) {
                (window as any).googleTranslateElementInit = () => {
                    this.createGoogleTranslateWidget();
                    // Resolve after widget creation
                    setTimeout(() => resolve(), 100);
                };
            }
            
            const existingScript = document.getElementById(this.googleTranslateScriptId);
            
            if (existingScript) {
                // Script already exists, check if Google Translate is loaded
                if ((window as any).google?.translate?.TranslateElement) {
                    // Check again if widget exists
                    if (!document.querySelector('.goog-te-combo')) {
                        this.createGoogleTranslateWidget();
                    }
                    resolve();
                } else {
                    // Wait for it to load
                    const checkInterval = setInterval(() => {
                        if ((window as any).google?.translate?.TranslateElement) {
                            clearInterval(checkInterval);
                            if (!document.querySelector('.goog-te-combo')) {
                                this.createGoogleTranslateWidget();
                            }
                            resolve();
                        }
                    }, 100);

                    // Timeout after 5 seconds
                    setTimeout(() => {
                        clearInterval(checkInterval);
                        resolve();
                    }, 5000);
                }
                return;
            }

            // Create and load script
            const script = document.createElement('script');
            script.id = this.googleTranslateScriptId;
            script.type = 'text/javascript';
            script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';

            script.onload = () => {
                // Don't resolve immediately, wait for the callback
                if (!(window as any).google?.translate?.TranslateElement) {
                    // Wait for the API to be ready
                    const waitInterval = setInterval(() => {
                        if ((window as any).google?.translate?.TranslateElement) {
                            clearInterval(waitInterval);
                            resolve();
                        }
                    }, 100);
                    
                    setTimeout(() => {
                        clearInterval(waitInterval);
                        resolve();
                    }, 5000);
                } else {
                    resolve();
                }
            };

            script.onerror = () => {
                resolve();
            };

            document.head.appendChild(script);
        });
    }

    // Create the Google Translate widget
    private createGoogleTranslateWidget(): void {
        try {
            // Check if widget already exists
            const existingSelect = document.querySelector('.goog-te-combo');
            if (existingSelect) {
                this.isInitialized = true;
                return;
            }
            
            const google = (window as any).google;
            if (!google?.translate?.TranslateElement) {
                return;
            }

            // Ensure container exists
            let container = document.getElementById(this.googleTranslateElementId);
            if (!container) {
                this.ensureContainerExists();
                container = document.getElementById(this.googleTranslateElementId);
            }
            
            if (container) {
                // Only clear if there's no widget already
                if (!container.querySelector('.goog-te-gadget')) {
                    container.innerHTML = '';
                    
                    new google.translate.TranslateElement(
                        {
                            pageLanguage: 'en',
                            includedLanguages: 'en,es',
                            layout: google.translate.TranslateElement.InlineLayout.HORIZONTAL,
                            autoDisplay: false,
                            multilanguagePage: true,
                            gaTrack: false,
                        },
                        this.googleTranslateElementId
                    );
                }
                
                // Mark as initialized after a short delay
                setTimeout(() => {
                    const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
                    if (selectElement && selectElement.options.length > 0) {
                        this.isInitialized = true;
                    } else if (selectElement) {
                        // Wait for options to populate
                        setTimeout(() => {
                            const select = document.querySelector('.goog-te-combo') as HTMLSelectElement;
                            if (select && select.options.length > 0) {
                                this.isInitialized = true;
                            }
                        }, 1000);
                    }
                }, 500);
            }

        } catch (error) {
            // Widget creation failed
        }
    }

    // Wait for Google Translate to be ready
    private waitForGoogleTranslate(): Promise<boolean> {
        return new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 30; // 15 seconds

            const checkReady = () => {
                const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
                if (selectElement) {
                    resolve(true);
                } else if (attempts < maxAttempts) {
                    attempts++;
                    setTimeout(checkReady, 500);
                } else {
                    resolve(false);
                }
            };

            // Give it a moment for the widget to render
            setTimeout(checkReady, 100);
        });
    }

    // Apply a specific language
    applyLanguage(languageCode?: string): void {
        // Ensure languageCode is always a string
        const lang = languageCode || localStorage.getItem('selectedLanguage') || 'en';
        
        // Set flag to prevent helper from interfering
        sessionStorage.setItem('changingLanguage', 'true');

        // Wait a bit for the widget to be ready
        const attemptApply = (retries = 0) => {
            const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (!selectElement) {
                if (retries < 20) {
                    setTimeout(() => attemptApply(retries + 1), 500);
                }
                return;
            }

            // Check if options are populated
            if (selectElement.options.length === 0) {
                if (retries < 20) {
                    setTimeout(() => attemptApply(retries + 1), 500);
                }
                return;
            }

            try {
                // Handle English selection (original language) - Production Ready
                if (lang === 'en') {
                    // Save preference IMMEDIATELY to prevent conflicts
                    localStorage.setItem('selectedLanguage', 'en');
                    
                    // Production-ready approach: Use a combination of techniques
                    
                    // Step 1: Clear the translation cookie properly
                    this.clearTranslationCookies();
                    
                    // Step 2: Set a marker cookie that tells Google Translate to not translate
                    // Using the specific format Google Translate expects
                    document.cookie = 'googtrans=; path=/';
                    document.cookie = 'googtrans=/en/en; path=/';
                    
                    
                    // Try different values for English
                    // Google Translate might use 'en', empty string, or a special value
                    const englishValues = ['en', '', 'en-US'];
                    let englishValue = '';
                    
                    // Find which value represents English
                    for (const opt of selectElement.options) {
                        if (opt.text.toLowerCase().includes('english') || 
                            opt.text.toLowerCase().includes('original') ||
                            opt.value === 'en' || 
                            opt.value === '') {
                            englishValue = opt.value;
                            break;
                        }
                    }
                    
                    // If we didn't find English, default to empty
                    if (englishValue === undefined) {
                        englishValue = '';
                    }
                    
                    // Set to English value
                    selectElement.value = englishValue;
                    
                    // Dispatch change event
                    if (document.createEvent) {
                        const evt = document.createEvent('HTMLEvents');
                        evt.initEvent('change', true, true);
                        selectElement.dispatchEvent(evt);
                    } else {
                        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    
                    // Step 4: Monitor and maintain the English state
                    // This prevents Google Translate from auto-reverting
                    let monitorCount = 0;
                    const monitorInterval = setInterval(() => {
                        monitorCount++;
                        
                        // Check if Google Translate tried to change it back
                        const currentLang = selectElement.value;
                        const currentCookie = document.cookie.match(/googtrans=([^;]*)/);
                        
                        // Find the English value again (in case it changed)
                        let englishVal = '';
                        for (const opt of selectElement.options) {
                            if (opt.text.toLowerCase().includes('english') || 
                                opt.text.toLowerCase().includes('original') ||
                                opt.value === 'en' || 
                                opt.value === '') {
                                englishVal = opt.value;
                                break;
                            }
                        }
                        
                        // If the select value was changed from English
                        if (currentLang !== englishVal && currentLang !== 'en') {
                            // Force back to English
                            selectElement.value = englishVal;
                            document.cookie = 'googtrans=/en/en; path=/';
                            
                            // Dispatch change event again
                            if (document.createEvent) {
                                const evt = document.createEvent('HTMLEvents');
                                evt.initEvent('change', true, true);
                                selectElement.dispatchEvent(evt);
                            } else {
                                selectElement.dispatchEvent(new Event('change', { bubbles: true }));
                            }
                            
                            // Force untranslate if still translated
                            if (document.documentElement.classList.contains('translated-ltr') || 
                                document.documentElement.classList.contains('translated-rtl')) {
                                forceUntranslate();
                            }
                        }
                        
                        // Check if we successfully reverted to English
                        const isTranslated = document.documentElement.classList.contains('translated-ltr') || 
                                           document.documentElement.classList.contains('translated-rtl');
                        
                        // Stop monitoring after 3 seconds or when successfully reverted
                        if (monitorCount >= 15 || !isTranslated) {
                            clearInterval(monitorInterval);
                            // Clear the changing flag
                            sessionStorage.removeItem('changingLanguage');
                        }
                    }, 200);
                    
                    // Step 5: As a fallback, remove translation attributes
                    setTimeout(() => {
                        // Remove Google Translate added attributes and classes
                        document.documentElement.classList.remove('translated-ltr', 'translated-rtl');
                        document.body.classList.remove('translated-ltr', 'translated-rtl');
                        
                        // Remove translate attribute from elements
                        const translatedElements = document.querySelectorAll('[translate]');
                        translatedElements.forEach(el => {
                            el.removeAttribute('translate');
                        });
                    }, 1000);
                    
                } else {
                    // Find the option for the language
                    const optionExists = Array.from(selectElement.options).some(opt => opt.value === lang);
                    
                    if (!optionExists) {
                        // Default to Spanish if the requested language isn't available
                        selectElement.value = 'es';
                        document.cookie = 'googtrans=/en/es;path=/';
                    } else {
                        // Apply translation
                        this.clearTranslationCookies();
                        document.cookie = `googtrans=/en/${lang};path=/`;
                        selectElement.value = lang;
                    }
                    
                    // Dispatch change event to trigger translation
                    if (document.createEvent) {
                        const evt = document.createEvent('HTMLEvents');
                        evt.initEvent('change', true, true);
                        selectElement.dispatchEvent(evt);
                    } else {
                        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
                
                
                // Save preference
                localStorage.setItem('selectedLanguage', lang);
                
                // Clear the changing flag after successful change
                setTimeout(() => {
                    sessionStorage.removeItem('changingLanguage');
                }, 1000);
            } catch (error) {
                sessionStorage.removeItem('changingLanguage');
            }
        };

        attemptApply();
    }

    // Clear translation cookies
    private clearTranslationCookies(): void {
        document.cookie.split(';').forEach((c) => {
            if (c.indexOf('googtrans') >= 0) {
                const eqPos = c.indexOf('=');
                const name = eqPos > -1 ? c.substring(0, eqPos).trim() : c.trim();
                document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`;
            }
        });
    }

    // Clean up Google Translate
    cleanup(): void {
        try {
            // Disconnect mutation observer
            if (this.mutationObserver) {
                this.mutationObserver.disconnect();
                this.mutationObserver = null;
            }
            
            // Clear translation cookies to revert to original text
            this.clearTranslationCookies();
            
            // Set cookie to explicitly use original language
            document.cookie = 'googtrans=/en/en;path=/;expires=' + new Date(0).toUTCString();
            document.cookie = 'googtrans=;path=/;expires=' + new Date(0).toUTCString();
            
            // Schedule DOM cleanup after React updates
            requestAnimationFrame(() => {
                // Clear widget content safely
                const container = document.getElementById(this.googleTranslateElementId);
                if (container && container.parentNode) {
                    // Mark for safe removal
                    container.setAttribute('data-removing', 'true');
                    // Use React-safe approach
                    try {
                        container.parentNode.removeChild(container);
                    } catch (e) {
                        // If React already removed it, that's fine
                    }
                }

                // Remove Google Translate elements from DOM safely
                const googleElements = document.querySelectorAll('.goog-te-banner-frame, .goog-te-menu-frame, .goog-te-balloon-frame, .goog-te-balloon');
                googleElements.forEach(el => {
                    try {
                        // Check if element still has parent before removal
                        if (el.parentNode) {
                            el.remove();
                        }
                    } catch (error) {
                        // Element might have already been removed, ignore the error
                    }
                });
                
                // Also remove any Google Translate specific classes from body
                document.body.classList.remove('translated-ltr', 'translated-rtl');
            });
            
            // Clear title protection
            if ((window as any).__titleProtectionInterval) {
                clearInterval((window as any).__titleProtectionInterval);
                (window as any).__titleProtectionInterval = null;
            }
            
            // Restore original title
            const originalTitle = sessionStorage.getItem('original_page_title');
            if (originalTitle) {
                document.title = originalTitle;
            }
            
            // Remove notranslate class
            document.documentElement.classList.remove('notranslate-title');

            // Reset state
            this.isInitialized = false;
            this.isInitializing = false;

            // Page reload is now handled by the logout epic
            // This prevents duplicate reloads
        } catch (error) {
            // Cleanup error
        }
    }

    // Check if initialized
    isReady(): boolean {
        return this.isInitialized && !!document.querySelector('.goog-te-combo');
    }

    // Protect container from React removal
    private protectContainer(): void {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
        
        // Create observer to watch for container removal
        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
                    mutation.removedNodes.forEach((node) => {
                        // If our container was removed, recreate it
                        if ((node as Element).id === this.googleTranslateElementId) {
                            // Disconnect observer temporarily
                            this.mutationObserver?.disconnect();
                            // Recreate container
                            setTimeout(() => {
                                this.ensureContainerExists();
                            }, 0);
                        }
                    });
                }
            });
        });
        
        // Start observing document.body for child removals
        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: false
        });
    }
    
    // Hide Google Translate UI elements
    hideUIElements(): void {
        const styleId = 'google-translate-hide-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .goog-te-banner-frame.skiptranslate { display: none !important; }
                body { top: 0px !important; }
                .goog-logo-link { display: none !important; }
                .goog-te-gadget { color: transparent !important; }
                .goog-te-gadget .goog-te-combo { margin: 0 !important; }
                #goog-gt-tt { display: none !important; }
                .goog-tooltip { display: none !important; }
                .goog-tooltip:hover { display: none !important; }
                .goog-text-highlight { background-color: transparent !important; border: none !important; box-shadow: none !important; }
                iframe.skiptranslate { visibility: hidden !important; }
                .goog-te-combo { opacity: 0; position: absolute; top: -9999px; left: -9999px; }
                iframe.skiptranslate, iframe[id^="goog-gt"], .goog-te-menu-frame { display: none !important; visibility: hidden !important; height: 0 !important; width: 0 !important; }
            `;
            document.head.appendChild(style);
        }
        
        // Protect the title from translation
        this.protectTitle();
    }
    
    // Protect title from Google Translate
    private protectTitle(): void {
        // Store original title
        const originalTitle = document.title;
        sessionStorage.setItem('original_page_title', originalTitle);
        
        // Add class to html element to exclude title
        document.documentElement.classList.add('notranslate-title');
        
        // Monitor and restore title if it gets translated
        let titleCheckInterval = setInterval(() => {
            const storedTitle = sessionStorage.getItem('original_page_title');
            if (storedTitle && document.title !== storedTitle) {
                // Check if title looks translated (contains unexpected characters or changed significantly)
                if (this.isInitialized && document.title !== storedTitle) {
                    document.title = storedTitle;
                }
            }
        }, 1000);
        
        // Store interval for cleanup
        (window as any).__titleProtectionInterval = titleCheckInterval;
    }

    // Show Google Translate UI elements (for debugging)
    showUIElements(): void {
        const style = document.getElementById('google-translate-hide-style');
        if (style && style.parentNode) {
            style.parentNode.removeChild(style);
        }
    }
}

export default GoogleTranslateManager;